// Quebec Law 25 compliant cookie-consent banner.
// Shows until the visitor grants or denies consent.
// - Grant  -> loads GA4 + Meta Pixel (if IDs are set in env).
// - Deny   -> GA/Meta stay dormant. First-party analytics keeps running
//              (legitimate interest — anonymous session_id only).

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { denyConsent, getConsent, grantConsent } from "@/lib/analytics";

export default function CookieConsent() {
  const { lang } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Delay first paint slightly so the banner never blocks the hero LCP.
    const t = setTimeout(() => {
      if (getConsent() === "unknown") setVisible(true);
    }, 600);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  const t = lang === "fr" ? {
    title: "Confidentialité et témoins",
    body:
      "Nous utilisons des témoins pour analyser le trafic et améliorer votre expérience. Certains témoins optionnels alimentent Google Analytics et Meta Pixel. Vous pouvez tout accepter, tout refuser, ou consulter notre politique.",
    accept: "Tout accepter",
    reject: "Refuser les optionnels",
    learn: "Politique de confidentialité",
  } : {
    title: "Privacy and cookies",
    body:
      "We use cookies to analyze traffic and improve your experience. Optional cookies power Google Analytics and Meta Pixel. You can accept all, reject optional, or read our policy.",
    accept: "Accept all",
    reject: "Reject optional",
    learn: "Privacy policy",
  };

  const onAccept = () => {
    grantConsent();
    setVisible(false);
  };
  const onReject = () => {
    denyConsent();
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t.title}
      className="fixed bottom-3 right-3 z-[9998] max-w-sm rounded-2xl border bg-white/95 p-3 shadow-lg backdrop-blur sm:bottom-4 sm:right-4"
      style={{ borderColor: "rgba(15, 23, 42, 0.12)" }}
      data-testid="cookie-consent-banner"
    >
      <div className="flex items-start gap-2.5">
        <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600">
          <Cookie className="h-3.5 w-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold text-slate-900">{t.title}</p>
          <p className="mt-1 text-[11px] leading-relaxed text-slate-600">
            {lang === "fr"
              ? "Analyse de trafic anonyme. Acceptez pour activer GA4 + Meta Pixel."
              : "Anonymous traffic analytics. Accept to enable GA4 + Meta Pixel."}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <Button
              type="button"
              onClick={onAccept}
              size="sm"
              className="h-7 bg-blue-600 px-3 text-[11px] font-semibold uppercase tracking-wider text-white hover:bg-blue-700"
              data-testid="cookie-consent-accept"
            >
              {t.accept}
            </Button>
            <Button
              type="button"
              onClick={onReject}
              variant="outline"
              size="sm"
              className="h-7 px-3 text-[11px] font-semibold uppercase tracking-wider"
              data-testid="cookie-consent-reject"
            >
              {t.reject}
            </Button>
            <Link
              to="/approval/privacy"
              className="ml-0.5 text-[10px] font-semibold text-blue-600 hover:underline"
              data-testid="cookie-consent-learn-more"
            >
              {t.learn}
            </Link>
          </div>
        </div>
        <button
          type="button"
          onClick={onReject}
          className="text-slate-400 hover:text-slate-700"
          aria-label={t.reject}
          data-testid="cookie-consent-close"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
